import {
	Box,
	Typography,
	Alert,
	Card,
	CardActionArea,
	CardContent,
	Divider,
	Chip,
	List,
	ListItem,
	ListItemText,
} from "@mui/material";
import type { SummaryProps } from "./Summary.types";
import { WIZARD_STEP_ICONS } from "../../WizardContainer/WizardContainer.types";
import { useSummaryViewModel } from "./Summary.vm";

// ============================================================================
// Component
// ============================================================================

export function Summary(props: SummaryProps) {
	const vm = useSummaryViewModel(props);

	return (
		<Box>
			{/* Stats Cards */}
			<Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
				<Card sx={{ minWidth: 150 }}>
					<CardContent>
						<Typography variant="h4" color="primary" data-testid="jobs-count">
							{vm.jobCount}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Total Jobs
						</Typography>
					</CardContent>
				</Card>

				<Card sx={{ minWidth: 150 }}>
					<CardContent>
						<Typography variant="h4" color="success.main" data-testid="skipped-count">
							{vm.skippedCount}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Issues Skipped
						</Typography>
					</CardContent>
				</Card>

				{vm.remainingIssues > 0 && (
					<Card sx={{ minWidth: 150 }}>
						<CardContent>
							<Typography variant="h4" color="warning.main" data-testid="remaining-count">
								{vm.remainingIssues}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Remaining Issues
							</Typography>
						</CardContent>
					</Card>
				)}
			</Box>

			{/* Skipped Issues Grid */}
			{vm.skippedIssueList.length > 0 && (
				<>
					<Typography variant="subtitle1" gutterBottom>
						Skipped Issues ({vm.skippedIssueList.length})
					</Typography>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
							gap: 2,
							mb: 2,
						}}
					>
						{vm.skippedIssueList.map((item) => (
							<Card key={item.issueId} sx={{ height: "100%" }}>
								<CardActionArea
									onClick={() => vm.handleIssueClick(item)}
									data-testid={`skipped-issue-${item.issueId}`}
									sx={{ height: "100%" }}
								>
									<CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
											{(() => {
												const Icon = WIZARD_STEP_ICONS[item.step];
												return <Icon color="warning" sx={{ fontSize: 18 }} />;
											})()}
											<Typography variant="body2" fontWeight={500}>
												{item.stepTitle}
											</Typography>
										</Box>
										{item.jobs.map((j, idx) => (
											<Typography key={idx} variant="caption" color="text.secondary" component="div">
												{[j.grower, j.farm, j.field].filter(Boolean).join(" / ")}
											</Typography>
										))}
									</CardContent>
								</CardActionArea>
							</Card>
						))}
					</Box>
				</>
			)}

			<Divider sx={{ my: 2 }} />

			{/* Jobs by Grower */}
			<Typography variant="subtitle1" gutterBottom>
				Jobs by Grower
			</Typography>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
					gap: 2,
				}}
			>
				{Object.entries(vm.jobsByGrower).map(([growerName, growerJobs]) => (
					<Card key={growerName} sx={{ height: "100%" }}>
						<CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
								<Typography variant="body1" fontWeight="medium">
									{growerName}
								</Typography>
								<Chip label={`${growerJobs.length} job${growerJobs.length !== 1 ? "s" : ""}`} size="small" />
							</Box>
							<List dense sx={{ py: 0 }}>
								{growerJobs.slice(0, 3).map((job) => (
									<ListItem key={job.id} sx={{ py: 0, px: 0 }}>
										<ListItemText
											primary={job.fieldName || job.importFieldName || "Unknown Field"}
											secondary={job.farmName || job.importFarmName || "Unknown Farm"}
											primaryTypographyProps={{ variant: "body2" }}
											secondaryTypographyProps={{ variant: "caption" }}
										/>
									</ListItem>
								))}
								{growerJobs.length > 3 && (
									<ListItem sx={{ py: 0, px: 0 }}>
										<ListItemText
											primary={`... and ${growerJobs.length - 3} more`}
											primaryTypographyProps={{ variant: "caption", color: "text.secondary" }}
										/>
									</ListItem>
								)}
							</List>
						</CardContent>
					</Card>
				))}
			</Box>

			{vm.jobCount === 0 && (
				<Alert severity="info" sx={{ mt: 2 }}>
					<Typography>No jobs to load. All jobs may have been deleted during issue resolution.</Typography>
				</Alert>
			)}
		</Box>
	);
}
